type ReadJob = { controller: AbortController; promise: Promise<unknown>; subscribers: number };

// Share concurrent JSON reads only. Finished responses are never cached.
export function createApiReadCoordinator(timeoutMs = 60_000) {
  const jobs = new Map<string, ReadJob>();
  let generation = 0;
  return {
    invalidate() { generation++; jobs.clear(); },
    read<T>(key: string, send: (signal: AbortSignal) => Promise<T>, signal?: AbortSignal | null): Promise<T> {
      if (signal?.aborted) return Promise.reject(signal.reason);
      const scopedKey = `${generation}:${key}`;
      let job = jobs.get(scopedKey);
      if (!job) {
        const controller = new AbortController();
        let timer: ReturnType<typeof setTimeout>;
        const deadline = new Promise<never>((_, reject) => {
          const abort = () => reject(controller.signal.reason);
          controller.signal.addEventListener("abort", abort, { once: true });
          timer = setTimeout(() => controller.abort(new DOMException("Read timed out", "TimeoutError")), timeoutMs);
        });
        const created: ReadJob = { controller, subscribers: 0, promise: Promise.resolve(null) };
        let read: Promise<T>;
        try { read = send(controller.signal); } catch (error) { read = Promise.reject(error); }
        created.promise = Promise.race([read, deadline]).finally(() => {
          clearTimeout(timer);
          if (jobs.get(scopedKey) === created) jobs.delete(scopedKey);
        });
        job = created;
        jobs.set(scopedKey, job);
      }
      const shared = job;
      shared.subscribers++;
      return new Promise<T>((resolve, reject) => {
        let settled = false;
        const finish = (callback: () => void) => {
          if (settled) return;
          settled = true;
          signal?.removeEventListener("abort", abort);
          shared.subscribers--;
          callback();
          if (!shared.subscribers) {
            if (jobs.get(scopedKey) === shared) jobs.delete(scopedKey);
            shared.controller.abort(new DOMException("Read cancelled", "AbortError"));
          }
        };
        const abort = () => finish(() => reject(signal?.reason));
        signal?.addEventListener("abort", abort, { once: true });
        shared.promise.then(value => finish(() => {
          try { resolve(structuredClone(value) as T); } catch (error) { reject(error); }
        }), error => finish(() => reject(error)));
      });
    },
  };
}
